"use client";

import Link from "next/link";

export default function CustomPricingCard({ ctaHref }) {
  return (
    <section className=" flex flex-col items-center justify-center  text-white px-4 md:py-16 py-8 md:mt-12  relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-700/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-700/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Header */}
      <div className="text-center mb-12 relative z-10">
        <div className="inline-block mb-4">
          <span className="text-sm font-semibold text-white bg-blue-700 px-5 py-2 rounded-full border border-blue-700 transition hover:bg-blue-500">
            Enterprise Solution
          </span>
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 text-white">
          Choose your custom plan
        </h1>
        <p className="text-lg text-neutral-300 max-w-2xl mx-auto">
          Get a tailored solution designed specifically for your business needs.
        </p>
      </div>

      {/* Horizontal Pricing Card */}
      <div className="relative z-10 max-w-6xl w-full">
        <div className="bg-gradient-to-br from-neutral-900/90 via-neutral-900/80 to-neutral-950/90 backdrop-blur-sm border border-neutral-800/50 rounded-3xl p-6 md:p-8 lg:p-10 shadow-2xl shadow-blue-700/10 hover:shadow-blue-700/20 transition-all duration-500 hover:border-blue-700 group">
          {/* Glow effect */}
          <div className="absolute inset-0 bg-blue-700/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          
          <div className="relative z-10 flex flex-col lg:flex-row gap-8 lg:gap-12">
            {/* Left Section - Content */}
            <div className="flex-1 flex flex-col">
              {/* Badge */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-700 border border-blue-700 flex items-center justify-center shadow-lg shadow-blue-700/30 transition hover:bg-blue-500">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold">Custom Enterprise Plan</h2>
                  <p className="text-sm text-blue-700 font-medium">Premium Tier</p>
                </div>
              </div>

              <p className="text-neutral-300 mb-6 text-base leading-relaxed">
                Perfect for large teams or unique project requirements. Built for scale, designed for success.
              </p>

              {/* Features */}
              <div className="flex-1">
                <h3 className="text-lg font-semibold mb-4 text-neutral-200">What's included:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    "Everything from Suite Enterprise",
                    "Custom API integrations",
                    "Advanced analytics & reporting",
                    "Dedicated account manager",
                    "Priority 24/7 support",
                    "Custom SLA agreements",
                    "White-label solutions",
                    "On-premise deployment options"
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 group/item">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-blue-700 border border-blue-700 flex items-center justify-center transition hover:bg-blue-500">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-neutral-300 text-sm group-hover/item:text-white transition-colors">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Section - Price & CTA */}
            <div className="lg:w-80 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-neutral-800 pt-6 lg:pt-0 lg:pl-8">
              {/* Price */}
              <div className="mb-6 lg:mb-8">
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-blue-700 text-2xl md:text-3xl font-semibold">$</span>
                  <span className="text-5xl md:text-6xl lg:text-7xl font-bold text-blue-700">—</span>
                </div>
                <p className="text-neutral-400 text-sm">Custom pricing based on your needs</p>
              </div>

              {/* CTA Button */}
              <div className="space-y-4">
                <Link href={ctaHref} >
                <button className="group/btn relative w-full bg-blue-700 border border-blue-700 px-5 py-2 text-sm text-white transition hover:bg-blue-500 font-semibold rounded-xl hover:scale-[1.02] active:scale-[0.98] overflow-hidden">
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    Contact us for details
                    <svg className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </button>
                </Link>

                {/* Footer note */}
                <p className="text-center text-xs text-neutral-100">
                  No credit card required • Schedule a consultation today
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
